import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { saveData } from 'services/api/saveData';
import { sanitizeTextareaFromString } from 'services/utils/convertTextareaData';
import CustomQuillEditor, { CustomQuillEditorHandle } from 'components/forms/fields/CustomQuillEditor';
import SplitButton from 'components/buttons/SplitButton';
import TaskForm from '../../views/tasks/TaskForm';
import { Ticket, TicketStatus, SELECTABLE_TICKET_STATUSES } from './TicketTypes';
import 'react-quill/dist/quill.snow.css';

// The composer modes, a note is internal, an email goes to the reporter and a task is a todo
type ComposerMode = 'note' | 'email' | 'task';

interface TicketComposerProps {
    ticket: Ticket;
    onSaved: () => void;
}

// The composer to add a note, send an email or create a task on a ticket
const TicketComposer: React.FC<TicketComposerProps> = ({ ticket, onSaved }) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const [mode, setMode] = useState<ComposerMode>('email');
    const [body, setBody] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [taskFormKey, setTaskFormKey] = useState(0);
    const editorRef = useRef<CustomQuillEditorHandle>(null);

    // The statuses offered by the button, an open ticket can no longer be set to open
    const availableStatuses = useMemo(() => (
        SELECTABLE_TICKET_STATUSES.filter(status => !(status === 'open' && ticket.status === 'open'))
    ), [ticket.status]);

    // Determine the default status of the main button, a note keeps the current status when it can be chosen
    const defaultStatus: TicketStatus = useMemo(() => {
        if (ticket.status === 'new') return 'open';
        if (mode === 'email') return ticket.status === 'open' ? 'awaiting' : 'open';
        return availableStatuses.includes(ticket.status) ? ticket.status : 'parked';
    }, [ticket.status, mode, availableStatuses]);

    // Build the label of a status action
    const getActionLabel = (status: TicketStatus) => {
        const prefix = mode === 'email' ? 'ticket.composer.send_as_label' : 'ticket.composer.save_as_label';
        return `${t(prefix)} ${t(`ticket.status.${status}`).toLowerCase()}`;
    };

    // Check wheter the editor body has content
    const hasBody = body.replace(/<(.|\n)*?>/g, '').trim().length > 0;

    // Save the note or send the email with the chosen status
    const handleSubmit = async (status: TicketStatus) => {
        // Validate the body
        if (!hasBody) {
            setErrors({ body: t('validation.general.required') });
            return;
        }
        setErrors({});
        setLoading(true);

        try {
            // Post the note or message to the ticket
            const apiUrl = mode === 'email' ? `post_ticket_message/${ticket.id}` : `post_ticket_note/${ticket.id}`;
            const data: Record<string, any> = { body: mode === 'note' ? sanitizeTextareaFromString(body) : body, status };
            const response = await saveData({ apiUrl, method: 'post', data });

            // Handle the response
            if (response && response.status === 201) {
                setBody('');
                setFloatingAlert({ type: 'success' });
                onSaved();
            } else if (response && response.data) {
                const responseErrors: Record<string, string> = {};
                Object.entries(response.data).forEach(([key, value]) => {
                    responseErrors[key] = t(String(Array.isArray(value) ? value[0] : value));
                });
                setErrors(responseErrors);
            }
        } catch (error: any) {
            // Show the validation errors of the backend
            const responseData = error?.response?.data;
            if (responseData && typeof responseData === 'object') {
                const responseErrors: Record<string, string> = {};
                Object.entries(responseData).forEach(([key, value]) => {
                    responseErrors[key] = t(String(Array.isArray(value) ? value[0] : value));
                });
                setErrors(responseErrors);
            } else {
                setFloatingAlert({ type: 'danger', message: 'ticket.composer.save_failed_message' });
            }
        } finally {
            setLoading(false);
        }
    };

    // Build the alternative status actions of the split button
    const alternativeItems = availableStatuses
        .filter(status => status !== defaultStatus)
        .map(status => ({ label: getActionLabel(status), onClick: () => handleSubmit(status) }));

    // Switch the composer mode and reset the errors
    const switchMode = (newMode: ComposerMode) => {
        setMode(newMode);
        setErrors({});
    };

    return (
        <div className='ticket-composer'>
            <ul className='ticket-composer-tabs'>
                <li className={mode === 'email' ? 'active' : ''} onClick={() => switchMode('email')}>
                    {t('ticket.composer.email_tab')}
                </li>
                <li className={mode === 'note' ? 'active' : ''} onClick={() => switchMode('note')}>
                    {t('ticket.composer.note_tab')}
                </li>
                <li className={mode === 'task' ? 'active' : ''} onClick={() => switchMode('task')}>
                    {t('ticket.composer.task_tab')}
                </li>
            </ul>

            {mode === 'task' ? (
                <div className='ticket-composer-task'>
                    <TaskForm
                        key={taskFormKey}
                        placement='inline'
                        timeline_page='ticket'
                        timeline_page_id={ticket.id}
                        closeForm={() => setMode('email')}
                        onSubmitSuccess={() => { setTaskFormKey(taskFormKey + 1); onSaved(); }}
                    />
                </div>
            ) : (
                <div className={`ticket-composer-body ${mode === 'note' ? 'note-mode' : 'email-mode'}`}
                     onClick={() => editorRef.current?.focus()}>
                    {errors.to && <p className='error-message'>{errors.to}</p>}
                    <CustomQuillEditor
                        key={mode}
                        ref={editorRef}
                        value={body}
                        onChange={setBody}
                        enableStyling='simple'
                        enableMentions={mode === 'note'}
                        className='note-form'
                        placeholder={t(mode === 'email' ? 'ticket.composer.email_placeholder' : 'ticket.composer.note_placeholder')}
                        identifier={`ticket-composer-${mode}`}
                    />
                    <div className='ticket-composer-actions'>
                        <div className='ticket-composer-errors'>
                            {errors.body && <p className='error-message'>{errors.body}</p>}
                        </div>
                        <div onClick={(event) => event.stopPropagation()}>
                            <SplitButton
                                label={getActionLabel(defaultStatus)}
                                onClick={() => handleSubmit(defaultStatus)}
                                items={alternativeItems}
                                loading={loading}
                                size='extra-small'
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketComposer;
